import { tv } from '../../utils/tv';

// Simple color names matching component color props
export type InteractionColor =
  | 'accent-primary'
  | 'accent-secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'static-white';

/**
 * Interaction colour configuration.
 * Each property maps to a known colour for the corresponding CSS property
 * (background, border-color, text colour).
 *
 * `bgMode` controls which set of background tokens is used on hover/pressed:
 * - `filled` — solid colour tokens (`*-main-hovered/pressed`). Use for components with a background fill.
 * - `blank` — transparent overlay tokens (`*-main-blank-hovered/pressed`). Use for components without a background.
 * - `elevated` — solid tint tokens (`*-main-blank-hovered-solid/pressed-solid`). Use for components on an elevated
 *   background, where the tint must be opaque.
 *
 * All class names are string literals inside the `tv()` definition
 * so Tailwind can scan them statically — no `@source inline()` needed.
 */
export type InteractionColors = {
  backgroundColor?: InteractionColor;
  /**
   * Controls which set of background tokens to use for hover/pressed states.
   * @default 'filled'
   */
  bgMode?: 'filled' | 'blank' | 'elevated';
  borderColor?: InteractionColor;
  textColor?: InteractionColor;
};

export type InteractionColorConfig = InteractionColors;

/**
 * Unified interaction styles: hover, pressed, disabled.
 *
 * Returns a class string applied directly to the interactive element.
 * The element must carry `data-hovered`, `data-pressed`, and `data-disabled`
 * attributes so the custom Tailwind variants (`hover:`, `pressed:`, `disabled:`) fire correctly.
 *
 * State priority (disabled > pressed > hover) is enforced by Interactable.
 *
 * All class names are string literals so Tailwind can scan them statically.
 *
 * @example
 * const cls = interactionStyles({ backgroundColor: 'accent-primary' });
 * <button className={cls} data-hovered data-pressed>Button</button>
 */
export const interactionStyles = tv({
  base: [
    'outline-none',
    'disabled:cursor-not-allowed',
    'disabled:disabled-foreground',
    'disabled:disabled-border',
  ],
  variants: {
    backgroundColor: {
      'accent-primary': '',
      'accent-secondary': '',
      success: '',
      error: '',
      info: '',
      warning: '',
      'static-white': '',
    },
    bgMode: {
      filled: '',
      blank: '',
      elevated: '',
    },
    borderColor: {
      'accent-primary': [
        'hover:not-disabled:not-data-[loading=true]:border-accent-primary-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:border-accent-primary-main-pressed',
      ],
      'accent-secondary': [
        'hover:not-disabled:not-data-[loading=true]:border-accent-secondary-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:border-accent-secondary-main-pressed',
      ],
      success: [
        'hover:not-disabled:not-data-[loading=true]:border-success-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:border-success-main-pressed',
      ],
      error: [
        'hover:not-disabled:not-data-[loading=true]:border-error-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:border-error-main-pressed',
      ],
      info: [
        'hover:not-disabled:not-data-[loading=true]:border-info-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:border-info-main-pressed',
      ],
      warning: [
        'hover:not-disabled:not-data-[loading=true]:border-warning-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:border-warning-main-pressed',
      ],
      'static-white': [
        'hover:not-disabled:not-data-[loading=true]:border-static-white-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:border-static-white-main-pressed',
      ],
    },
    textColor: {
      'accent-primary': [
        'hover:not-disabled:not-data-[loading=true]:text-accent-primary-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:text-accent-primary-main-pressed',
      ],
      'accent-secondary': [
        'hover:not-disabled:not-data-[loading=true]:text-accent-secondary-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:text-accent-secondary-main-pressed',
      ],
      success: [
        'hover:not-disabled:not-data-[loading=true]:text-success-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:text-success-main-pressed',
      ],
      error: [
        'hover:not-disabled:not-data-[loading=true]:text-error-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:text-error-main-pressed',
      ],
      info: [
        'hover:not-disabled:not-data-[loading=true]:text-info-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:text-info-main-pressed',
      ],
      warning: [
        'hover:not-disabled:not-data-[loading=true]:text-warning-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:text-warning-main-pressed',
      ],
      'static-white': [
        'hover:not-disabled:not-data-[loading=true]:text-static-white-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:text-static-white-main-pressed',
      ],
    },
  },
  compoundVariants: [
    {
      bgMode: 'filled',
      class: ['disabled:disabled-background'],
    },
    // ─── Filled bg (opaque base) ───
    {
      backgroundColor: 'accent-primary',
      bgMode: 'filled',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-accent-primary-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-accent-primary-main-pressed',
      ],
    },
    {
      backgroundColor: 'accent-secondary',
      bgMode: 'filled',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-accent-secondary-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-accent-secondary-main-pressed',
      ],
    },
    {
      backgroundColor: 'success',
      bgMode: 'filled',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-success-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-success-main-pressed',
      ],
    },
    {
      backgroundColor: 'error',
      bgMode: 'filled',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-error-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-error-main-pressed',
      ],
    },
    {
      backgroundColor: 'info',
      bgMode: 'filled',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-info-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-info-main-pressed',
      ],
    },
    {
      backgroundColor: 'warning',
      bgMode: 'filled',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-warning-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-warning-main-pressed',
      ],
    },
    {
      backgroundColor: 'static-white',
      bgMode: 'filled',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-static-white-main-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-static-white-main-pressed',
      ],
    },
    // ─── Blank/ghost bg (transparent base — no disabled background) ───
    {
      backgroundColor: 'accent-primary',
      bgMode: 'blank',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-accent-primary-main-blank-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-accent-primary-main-blank-pressed',
      ],
    },
    {
      backgroundColor: 'accent-secondary',
      bgMode: 'blank',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-accent-secondary-main-blank-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-accent-secondary-main-blank-pressed',
      ],
    },
    {
      backgroundColor: 'success',
      bgMode: 'blank',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-success-main-blank-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-success-main-blank-pressed',
      ],
    },
    {
      backgroundColor: 'error',
      bgMode: 'blank',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-error-main-blank-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-error-main-blank-pressed',
      ],
    },
    {
      backgroundColor: 'info',
      bgMode: 'blank',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-info-main-blank-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-info-main-blank-pressed',
      ],
    },
    {
      backgroundColor: 'warning',
      bgMode: 'blank',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-warning-main-blank-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-warning-main-blank-pressed',
      ],
    },
    {
      backgroundColor: 'static-white',
      bgMode: 'blank',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-static-white-main-blank-hovered',
        'pressed:not-disabled:not-data-[loading=true]:bg-static-white-main-blank-pressed',
      ],
    },
    // ─── Elevated bg (solid tint — for ghost/outlined buttons on elevated surfaces) ───
    {
      backgroundColor: 'accent-primary',
      bgMode: 'elevated',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-accent-primary-main-blank-hovered-solid',
        'pressed:not-disabled:not-data-[loading=true]:bg-accent-primary-main-blank-pressed-solid',
      ],
    },
    {
      backgroundColor: 'accent-secondary',
      bgMode: 'elevated',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-accent-secondary-main-blank-hovered-solid',
        'pressed:not-disabled:not-data-[loading=true]:bg-accent-secondary-main-blank-pressed-solid',
      ],
    },
    {
      backgroundColor: 'success',
      bgMode: 'elevated',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-success-main-blank-hovered-solid',
        'pressed:not-disabled:not-data-[loading=true]:bg-success-main-blank-pressed-solid',
      ],
    },
    {
      backgroundColor: 'error',
      bgMode: 'elevated',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-error-main-blank-hovered-solid',
        'pressed:not-disabled:not-data-[loading=true]:bg-error-main-blank-pressed-solid',
      ],
    },
    {
      backgroundColor: 'info',
      bgMode: 'elevated',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-info-main-blank-hovered-solid',
        'pressed:not-disabled:not-data-[loading=true]:bg-info-main-blank-pressed-solid',
      ],
    },
    {
      backgroundColor: 'warning',
      bgMode: 'elevated',
      class: [
        'hover:not-disabled:not-data-[loading=true]:bg-warning-main-blank-hovered-solid',
        'pressed:not-disabled:not-data-[loading=true]:bg-warning-main-blank-pressed-solid',
      ],
    },
  ],
  defaultVariants: { bgMode: 'filled' },
});
